import { useState, useEffect, useCallback } from 'react'
import { X, ExternalLink, Loader2 } from 'lucide-react'
import { knowledgeBaseApi } from '../../services/api'

export default function PdfViewerModal({ isOpen, onClose, s3Key, documentName, pageNumber }) {
  const [pdfUrl, setPdfUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchPresignedUrl = useCallback(async () => {
    if (!s3Key) return
    setLoading(true)
    setError(null)
    try {
      const response = await knowledgeBaseApi.getPresignedUrl(s3Key)
      setPdfUrl(response.data.url)
    } catch (err) {
      setError('Failed to load document: ' + (err.response?.data?.message || err.message))
    } finally {
      setLoading(false)
    }
  }, [s3Key])

  useEffect(() => {
    if (isOpen && s3Key) {
      setPdfUrl(null)
      fetchPresignedUrl()
    }
  }, [isOpen, s3Key, fetchPresignedUrl])

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-5xl h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800 truncate">
            {documentName || s3Key?.split('/').pop() || 'Document'}
          </h3>
          <div className="flex items-center gap-2">
            {pdfUrl && (
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                className="p-1.5 text-slate-500 hover:text-primary-600 hover:bg-slate-100 rounded transition-colors"
                title="Open in new tab">
                <ExternalLink size={16} />
              </a>
            )}
            <button onClick={onClose}
              className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* PDF Content via iframe — no CORS issues */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-500 bg-slate-100">
              <Loader2 size={24} className="animate-spin" />
              <span className="text-sm">Loading document...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full bg-slate-100">
              <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{error}</p>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pageNumber ? `${pdfUrl}#page=${pageNumber}` : pdfUrl}
              title={documentName || 'PDF Viewer'}
              className="w-full h-full border-0"
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
